import { apiService } from "@/api/apiService";
import { Copy, DocumentPdf, Send } from "@/coral/imports/bundleicons";
import ChatInput from "@/coral/modules/ChatInput";
import { ConversationMessage, ConversationRole, MessageType } from "@/models/plan";
import { NewTaskService, TaskService } from "@/services";
import { ConversationService } from "@/services/ConversationService";
import { clearRouterState } from "@/utils/routerState";
import {
    Body1,
    Button,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Tag
} from "@fluentui/react-components";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
import rehypePrism from "rehype-prism";
import remarkGfm from "remark-gfm";
import "../../styles/Chat.css";
import "../../styles/PlanChat.css";
import "../../styles/prism-material-oceanic.css";
import FileUploadButton from "../FileUploadButton";
import { useInlineToaster } from "../toast/InlineToaster";
import { markdownComponents } from "./markdownComponents";
import PlanCard from "./PlanCard";

interface ConversationViewProps {
    sessionId: string;
}

const LOADING_MESSAGES = [
    "Thinking...",
    "Searching documentation...",
    "Consulting knowledge base...",
    "Processing your request...",
];

/**
 * A backend run routinely outlives any single HTTP connection: App Service severs an
 * idle request at roughly 5 minutes, while a plan with tool calls can legitimately run
 * for ten. Messages are therefore sent fire-and-forget - the POST returns 202 as soon as
 * the work is queued - and the answer is collected by polling conversation history. The
 * backend writes it to Cosmos whether or not the browser is still connected, so nothing
 * depends on the connection surviving.
 *
 * This is the longest a single run is given before we stop watching for it.
 */
const MAX_POLL_MS = 15 * 60 * 1000;

/** Tight at first so short answers still feel immediate, then backing off. */
const getPollingInterval = (elapsedSeconds: number): number => {
    if (elapsedSeconds < 15) return 1500;
    if (elapsedSeconds < 30) return 3000;
    if (elapsedSeconds < 120) return 5000;
    if (elapsedSeconds < 300) return 10000;
    return 15000;
};

/** The assistant messages already on screen, used as the "before" mark for a poll. */
const assistantMessageIds = (msgs: ConversationMessage[]): Set<string> =>
    new Set(msgs.filter(m => m.role === ConversationRole.ASSISTANT).map(m => m.id));

/**
 * Surface the server's own explanation instead of a generic string. apiClient rejects
 * with the raw response body, which for our API is {"detail": "..."} - without this a
 * content-safety block is indistinguishable from any other failure.
 */
const extractErrorDetail = (error: any, fallback: string): string => {
    const raw = typeof error?.message === "string" ? error.message : "";
    // Anything long is a proxy/HTML error page rather than one of our messages.
    if (!raw || raw.length > 300) return fallback;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.detail === "string" && parsed.detail) return parsed.detail;
    } catch {
        // Not JSON - fall through and show the raw text.
    }
    return raw;
};

/**
 * Export security assessment report to PDF
 */
const exportToPDF = async (content: string, agentName: string) => {
    try {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF();

        // Add header
        doc.setFontSize(16);
        doc.text('Cloud Architecture Security Assessment', 20, 20);
        doc.setFontSize(10);
        doc.text(`Generated by ${agentName || 'Architecture Agent'}`, 20, 30);
        doc.text(`Date: ${new Date().toLocaleString()}`, 20, 35);
        doc.text('', 20, 40); // spacer

        // CRITICAL: Strip all problematic Unicode that causes jsPDF corruption
        let plainText = content
            // Remove Unicode citation brackets and daggers (【4:18†source】)
            .replace(/【[^】]*】/g, (match) => {
                // Extract the content and convert to [source]
                const sourceMatch = match.match(/(\d+):(\d+)/);
                return sourceMatch ? `[${sourceMatch[0]}†source]` : '[source]';
            })
            .replace(/†/g, '')  // Remove dagger symbol
            .replace(/【/g, '[')  // Replace Unicode brackets
            .replace(/】/g, ']')
            // Remove zero-width characters and other invisible Unicode
            .replace(/[\u200B-\u200D\uFEFF]/g, '')
            // Remove all emojis (already replaced with text earlier)
            .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
            .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
            .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
            .replace(/[\u{2600}-\u{26FF}]/gu, '')
            .replace(/[\u{2700}-\u{27BF}]/gu, '')
            // Decode HTML entities
            .replace(/&amp;/gi, '&')
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'")
            .replace(/&nbsp;/gi, ' ')
            .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
            // Remove any remaining ampersands between characters
            .replace(/([^&])&+([^&])/g, '$1$2')
            .replace(/([^&])&+([^&])/g, '$1$2')
            .replace(/&+/g, '')
            // Convert to ASCII-safe characters only - strip anything above char code 127
            .replace(/[^\x00-\x7F]/g, (char) => {
                // Keep common safe characters, replace others with space or remove
                if (char === '\u2022') return '- '; // bullet
                if (char === '\u2013' || char === '\u2014') return '-'; // en-dash, em-dash
                if (char === '\u201C' || char === '\u201D') return '"'; // left/right double quote
                if (char === '\u2018' || char === '\u2019') return "'"; // left/right single quote
                return ''; // Remove unknown Unicode
            });

        // Step 4: Process line by line for proper formatting
        const lines = plainText.split('\n');
        const formattedLines: Array<{ text: string, fontSize: number, bold: boolean, indent: number }> = [];

        lines.forEach(line => {
            const trimmed = line.trim();
            if (!trimmed) {
                formattedLines.push({ text: '', fontSize: 10, bold: false, indent: 0 });
                return;
            }

            // Headers
            if (trimmed.startsWith('###')) {
                formattedLines.push({ text: '', fontSize: 10, bold: false, indent: 0 });
                formattedLines.push({ text: trimmed.replace(/^###\s*/, ''), fontSize: 11, bold: true, indent: 0 });
            } else if (trimmed.startsWith('##')) {
                formattedLines.push({ text: '', fontSize: 10, bold: false, indent: 0 });
                formattedLines.push({ text: trimmed.replace(/^##\s*/, ''), fontSize: 12, bold: true, indent: 0 });
            } else if (trimmed.startsWith('#')) {
                formattedLines.push({ text: '', fontSize: 10, bold: false, indent: 0 });
                formattedLines.push({ text: trimmed.replace(/^#\s*/, ''), fontSize: 14, bold: true, indent: 0 });
            }
            // Bullet points
            else if (trimmed.match(/^[-*]\s+/)) {
                const text = trimmed.replace(/^[-*]\s+/, '• ');
                formattedLines.push({ text, fontSize: 10, bold: false, indent: 10 });
            }
            // Numbered lists
            else if (trimmed.match(/^\d+\.\s+/)) {
                formattedLines.push({ text: trimmed, fontSize: 10, bold: false, indent: 10 });
            }
            // Bold text
            else if (trimmed.match(/\*\*.*\*\*/)) {
                const text = trimmed.replace(/\*\*/g, '');
                formattedLines.push({ text, fontSize: 10, bold: true, indent: 0 });
            }
            // Regular text
            else {
                const text = trimmed
                    .replace(/\*\*(.+?)\*\*/g, '$1')
                    .replace(/\*(.+?)\*/g, '$1')
                    .replace(/`(.+?)`/g, '$1');
                formattedLines.push({ text, fontSize: 10, bold: false, indent: 0 });
            }
        });

        // Render to PDF with proper formatting
        let y = 45;
        const lineHeight = 6;
        const maxWidth = 170;
        const pageHeight = doc.internal.pageSize.height - 20;

        // Manual text wrapping to avoid jsPDF splitTextToSize bug
        const wrapText = (text: string, maxWidth: number, fontSize: number): string[] => {
            doc.setFontSize(fontSize);
            const words = text.split(' ');
            const lines: string[] = [];
            let currentLine = '';

            words.forEach(word => {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const textWidth = doc.getTextWidth(testLine);

                if (textWidth > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine = testLine;
                }
            });

            if (currentLine) {
                lines.push(currentLine);
            }

            return lines.length > 0 ? lines : [''];
        };

        formattedLines.forEach(line => {
            // Handle page breaks
            if (y > pageHeight) {
                doc.addPage();
                y = 20;
            }

            // Apply formatting
            doc.setFontSize(line.fontSize);
            if (line.bold) {
                doc.setFont('helvetica', 'bold');
            } else {
                doc.setFont('helvetica', 'normal');
            }

            // Manually wrap text (avoiding jsPDF splitTextToSize which causes corruption)
            const wrappedLines = wrapText(line.text, maxWidth - line.indent, line.fontSize);
            wrappedLines.forEach((wrappedLine: string) => {
                if (y > pageHeight) {
                    doc.addPage();
                    y = 20;
                }
                doc.text(wrappedLine, 20 + line.indent, y);
                y += lineHeight;
            });
        });

        // Save with timestamp
        const filename = `security-assessment-${Date.now()}.pdf`;
        doc.save(filename);
    } catch (error) {
        console.error('Failed to generate PDF:', error);
        alert('Failed to generate PDF. Please try again.');
    }
};

/**
 * ConversationView - Component for continuous conversation with the agent
 * Supports both direct chat and plan creation/execution
 */
const ConversationView: React.FC<ConversationViewProps> = ({ sessionId }) => {
    const [messages, setMessages] = useState<ConversationMessage[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [inputHeight, setInputHeight] = useState(0);
    const [isProcessingInitialMessage, setIsProcessingInitialMessage] = useState(false);
    const [planCache, setPlanCache] = useState<Record<string, any>>({});
    const [loadingText, setLoadingText] = useState(LOADING_MESSAGES[0]);
    const [isFileAnalysis, setIsFileAnalysis] = useState(false);

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // Guards against a slow history request stacking up behind the interval timer.
    const pollInFlightRef = useRef(false);
    // Bumped whenever polling stops, so a response belonging to a superseded run is
    // discarded instead of writing a stale answer to the screen.
    const pollRunIdRef = useRef(0);
    const { showToast } = useInlineToaster();
    const location = useLocation();
    const navigate = useNavigate();

    // Handle initial message from HomePage
    useEffect(() => {
        const state = location.state as any;
        if (state?.isNewConversation && state?.initialMessage) {
            // Consume the state before doing anything with it, so a refresh of this
            // history entry falls through to loadConversationHistory instead of
            // re-sending a question the agent has already answered.
            clearRouterState();

            // Add optimistic user message immediately
            const optimisticUserMessage: ConversationMessage = {
                id: `temp-initial-${Date.now()}`,
                data_type: "conversation_message",
                session_id: sessionId,
                user_id: "",
                role: ConversationRole.USER,
                content: state.initialMessage,
                message_type: MessageType.TEXT,
                timestamp: new Date().toISOString(),
            };
            setMessages([optimisticUserMessage]);
            setIsProcessingInitialMessage(true);
            setLoading(true);

            let cancelled = false;

            // Brand new session, so there are no earlier assistant messages to exclude.
            startResponsePolling(new Set<string>(), Date.now(), () =>
                setIsProcessingInitialMessage(false)
            );

            // fire_and_forget for the same reason as handleSendMessage: a first question
            // can be just as long-running as any later one.
            ConversationService.sendMessage(sessionId, state.initialMessage, false, true)
                .catch((error) => {
                    if (cancelled) return;
                    console.error("Error sending initial message:", error);
                    stopPolling();
                    setLoading(false);
                    setIsProcessingInitialMessage(false);
                    showToast(extractErrorDetail(error, "Failed to send message"), "error");
                });

            return () => { cancelled = true; };
        }
    }, [sessionId, location.state]);

    // Load conversation history on mount (skip if it's a new conversation with initial message)
    useEffect(() => {
        const state = location.state as any;
        if (!state?.isNewConversation) {
            // Normal conversation - load history
            loadConversationHistory();
        } else if (state?.uploadedFile) {
            // File uploaded - automatically send analysis message
            console.log("File uploaded, auto-sending analysis request...");
            setIsFileAnalysis(true);
            setInitializing(false);

            // Clear the location state to prevent re-triggering
            clearRouterState();

            // Auto-send analysis request with RAI bypass flag
            setTimeout(async () => {
                try {
                    setLoading(true);
                    const analysisMessage = "UPLOADED ARCHITECTURE DOCUMENT: Perform a comprehensive cloud architecture security assessment analyzing security vulnerabilities, compliance with Ericsson policies, network security, identity & access controls, and data protection. Use Cloud_Architecture_Agent.";

                    console.log("Sending analysis message to backend...");

                    // Start polling BEFORE sending the message to catch responses quickly.
                    // This session was created by the upload, so it has no earlier
                    // assistant messages. isFileAnalysis is cleared when polling settles -
                    // it drives analysis-only UI and must not be reset by ordinary chat.
                    startResponsePolling(new Set<string>(), Date.now(), () =>
                        setIsFileAnalysis(false)
                    );

                    // Send analysis message (this is a fire-and-forget to avoid blocking)
                    // Note: For long-running analysis (>60s), the POST may timeout due to Azure App Service limits
                    // but the backend will continue processing. Polling will catch the result.
                    ConversationService.sendMessage(sessionId, analysisMessage, true, true)
                        .then(() => {
                            console.log("Analysis message sent successfully (or backend accepted the request)");
                        })
                        .catch((error) => {
                            console.warn("Analysis message POST timed out or failed, but polling will continue:", error);
                            // Don't stop polling or show error - the backend may still be processing
                            // The polling mechanism will detect when analysis completes
                        });

                } catch (error) {
                    console.error("Failed to trigger analysis:", error);
                    setLoading(false);
                    stopPolling();
                    setIsFileAnalysis(false);
                    showToast("Failed to start analysis. Please send a message manually.", "error");
                }
            }, 500); // Small delay to ensure session is ready
        } else {
            setInitializing(false);
        }
    }, [sessionId, location.pathname, location.state]);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Handle scroll button visibility
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            setShowScrollButton(scrollTop + clientHeight < scrollHeight - 100);
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    // Track input height for scroll button positioning
    useEffect(() => {
        if (inputContainerRef.current) {
            setInputHeight(inputContainerRef.current.offsetHeight);
        }
    }, [input]);

    // Cycle loading status text
    useEffect(() => {
        if (!loading) return;
        setLoadingText(LOADING_MESSAGES[0]);
        let i = 1;
        const interval = setInterval(() => {
            setLoadingText(LOADING_MESSAGES[i % LOADING_MESSAGES.length]);
            i++;
        }, 3000);
        return () => clearInterval(interval);
    }, [loading]);

    // Fetch plans for messages that have plan_id
    useEffect(() => {
        const fetchPlansForMessages = async () => {
            const planIds = messages
                .filter(msg => msg.plan_id && !planCache[msg.plan_id])
                .map(msg => msg.plan_id!);

            for (const planId of planIds) {
                try {
                    const result = await apiService.getPlanById(planId, true);
                    if (result?.plan_with_steps) {
                        setPlanCache(prev => ({
                            ...prev,
                            [planId]: result.plan_with_steps
                        }));
                    }
                } catch (error) {
                    console.error(`Error fetching plan ${planId}:`, error);
                }
            }
        };

        fetchPlansForMessages();
    }, [messages]);

    const loadConversationHistory = async () => {
        try {
            setInitializing(true);
            const history = await ConversationService.getHistory(sessionId);
            setMessages(history);
            resumePollingIfAwaitingAnswer(history);
        } catch (error) {
            console.error("Error loading conversation history:", error);
            showToast("Failed to load conversation history", "error");
        } finally {
            setInitializing(false);
        }
    };

    const checkForNewMessages = async () => {
        try {
            const history = await ConversationService.getHistory(sessionId);

            // Find messages that we don't have yet (by ID)
            const existingIds = new Set(messages.map(m => m.id));
            const newMessages = history.filter(m => !existingIds.has(m.id));

            // Only update if there are truly new messages
            if (newMessages.length > 0) {
                setMessages((prev: ConversationMessage[]) => [...prev, ...newMessages]);
            }
        } catch (error) {
            console.error("Error checking for new messages:", error);
        }
    };

    const stopPolling = () => {
        // Invalidate any in-flight poll so its response cannot update state.
        pollRunIdRef.current += 1;
        pollInFlightRef.current = false;
        if (pollingIntervalRef.current) {
            console.log("Stopping response polling");
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    /**
     * Watch conversation history until the answer to an in-flight message appears.
     *
     * @param baselineIds assistant messages already present. The run is complete once
     *   the server reports one that is NOT among them. Asking instead "is there any
     *   assistant message" - as this function previously did - is already true from the
     *   second turn onward, so it would resolve instantly and re-present the previous
     *   answer as the new one.
     * @param startedAtMs when the message was sent. A resumed poll passes the original
     *   send time so that refreshing the page cannot keep granting a fresh budget.
     * @param onSettled invoked once when polling stops, whatever the reason.
     */
    const startResponsePolling = (
        baselineIds: Set<string>,
        startedAtMs: number = Date.now(),
        onSettled?: () => void
    ) => {
        stopPolling();
        const runId = pollRunIdRef.current;
        let currentInterval = getPollingInterval(0);
        let pollCount = 0;

        const pollForMessages = async () => {
            if (pollInFlightRef.current) return;
            pollInFlightRef.current = true;
            try {
                pollCount++;
                const elapsedSeconds = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
                const history = await ConversationService.getHistory(sessionId);

                // This run was stopped or superseded while the request was in flight.
                if (runId !== pollRunIdRef.current) return;

                const answer = history.find(
                    msg => msg.role === ConversationRole.ASSISTANT && !baselineIds.has(msg.id)
                );

                if (answer) {
                    console.log(`Answer received after ${elapsedSeconds}s (poll ${pollCount})`);
                    // Note: history is written to the screen only here and on timeout.
                    // Doing it on every poll would briefly wipe the optimistic user
                    // message on any poll that lands before the backend has saved it.
                    setMessages(history);
                    setLoading(false);
                    stopPolling();
                    onSettled?.();
                    NewTaskService.refreshSessions();
                    setTimeout(() => NewTaskService.refreshSessions(), 2000);
                    return;
                }

                if (Date.now() - startedAtMs >= MAX_POLL_MS) {
                    console.warn(`Polling gave up after ${elapsedSeconds}s`);
                    setMessages(history);
                    setLoading(false);
                    stopPolling();
                    onSettled?.();
                    showToast(
                        "This is taking longer than expected. The answer is still being generated - reopen this conversation shortly to check.",
                        "warning"
                    );
                    return;
                }

                const newInterval = getPollingInterval(elapsedSeconds);
                if (newInterval !== currentInterval && pollingIntervalRef.current) {
                    currentInterval = newInterval;
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = setInterval(pollForMessages, currentInterval);
                }
            } catch (error) {
                // A single failed history read (token refresh, cold start) is not a
                // reason to abandon an answer that is still being generated.
                console.error("Error during polling:", error);
            } finally {
                // Only release the guard if this run still owns it.
                if (runId === pollRunIdRef.current) {
                    pollInFlightRef.current = false;
                }
            }
        };

        pollForMessages();
        pollingIntervalRef.current = setInterval(pollForMessages, currentInterval);
    };

    /**
     * Reattach to a run that was still in progress - typically the page was refreshed or
     * navigated away from while an answer was being generated. The backend writes to
     * Cosmos independently of the browser, so the answer is still coming; without this it
     * would sit unseen until the user happened to reopen the conversation.
     */
    const resumePollingIfAwaitingAnswer = (history: ConversationMessage[]) => {
        const last = history[history.length - 1];
        if (!last || last.role !== ConversationRole.USER) return;

        const sentAtMs = new Date(last.timestamp).getTime();
        if (!Number.isFinite(sentAtMs) || Date.now() - sentAtMs >= MAX_POLL_MS) return;

        console.log("Reattaching to an answer that was still in progress");
        setLoading(true);
        startResponsePolling(assistantMessageIds(history), sentAtMs);
    };

    // Stop polling when the conversation changes or the view unmounts.
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [sessionId]);

    const scrollToBottom = () => {
        messagesContainerRef.current?.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
        });
        setShowScrollButton(false);
    };

    const AGENT_SHORT_NAMES: Record<string, string> = {
        CLOUDPORTAL_DOCUMENTATION_AGENT: "Cloud Portal",
        CLOUD_PORTAL_AGENT: "Cloud Portal",
        AWS_DOCUMENTATION_AGENT: "AWS",
        AZURE_DOCUMENTATION_AGENT: "Azure",
        GCP_DOCUMENTATION_AGENT: "GCP",
        CLOUD_ARCHITECTURE_AGENT: "Cloud Architecture",
        AZURE_SUPPORT_AGENT: "Azure Support",
        GENERIC_AGENT: "Assistant",
        HUMAN_AGENT: "Assistant",
    };

    const formatAgentName = (agentName: string): string => {
        if (!agentName) return "Assistant";
        const key = agentName.toUpperCase().replace(/\s+/g, '_');
        if (AGENT_SHORT_NAMES[key]) return AGENT_SHORT_NAMES[key];
        return agentName
            .split('_')
            .filter(w => !['AGENT', 'DOCUMENTATION'].includes(w.toUpperCase()))
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ') || "Assistant";
    };

    const isCloudArchitectureMessage = (msg: ConversationMessage): boolean => {
        const source = msg.source || msg.metadata?.agent || "";
        return source.toLowerCase().includes('cloud_architecture') ||
            source.toLowerCase().includes('architecture') ||
            (msg.content && msg.content.includes('Security Assessment Score'));
    };

    const getAgentNameForMessage = (msg: ConversationMessage): string => {
        // First check if message has source field (directly from database)
        if (msg.source) {
            return formatAgentName(msg.source);
        }

        // Fallback: Check metadata
        if (msg.metadata?.agent) {
            return formatAgentName(msg.metadata.agent);
        }

        // Fallback: If message has a plan_id, try to get agent from cached plan
        if (msg.plan_id && planCache[msg.plan_id]) {
            const plan = planCache[msg.plan_id];
            if (plan.steps && plan.steps.length > 0) {
                // Get agent from first step
                return formatAgentName(plan.steps[0].agent);
            }
        }

        return "Assistant";
    };

    // Get agent name for loading indicator based on last assistant message
    const getLoadingAgentName = (): string => {
        // Find the most recent assistant message to get the agent source
        const lastAssistantMessage = [...messages]
            .reverse()
            .find(m => m.role === ConversationRole.ASSISTANT);

        if (lastAssistantMessage?.source) {
            return formatAgentName(lastAssistantMessage.source);
        }

        return "Assistant";
    };

    const formatMessageTimestamp = (timestamp: string): string => {
        try {
            const date = new Date(timestamp);
            return new Intl.DateTimeFormat(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            return '';
        }
    };

    const formatGenerationTime = (ms: number): string => {
        if (ms >= 60000) {
            const minutes = Math.floor(ms / 60000);
            const seconds = Math.round((ms % 60000) / 1000);
            return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
        }
        if (ms >= 1000) {
            return `${(ms / 1000).toFixed(1)}s`;
        }
        return `${ms}ms`;
    };

    const handleSendMessage = async () => {
        if (!input.trim() || loading) return;

        const userInput = input.trim();
        setInput("");
        setLoading(true);

        // Captured before sending: polling is done once the server reports an assistant
        // message that is not one of these.
        const baselineIds = assistantMessageIds(messages);

        // Optimistically add user message to UI
        const optimisticUserMessage: ConversationMessage = {
            id: `temp-${Date.now()}`,
            data_type: "conversation_message",
            session_id: sessionId,
            user_id: "", // Will be filled by backend
            role: ConversationRole.USER,
            content: userInput,
            message_type: MessageType.TEXT,
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, optimisticUserMessage]);
        startResponsePolling(baselineIds);

        try {
            // fire_and_forget: the backend acknowledges with 202 and keeps working, so a
            // long-running answer no longer depends on this connection staying open past
            // the ~5 minute infrastructure limit. Polling above collects the result.
            await ConversationService.sendMessage(sessionId, userInput, false, true);
        } catch (error) {
            // The request now returns in about a second, so a rejection means a genuine
            // failure (rejected by content safety, auth, backend down) rather than an
            // answer that is merely slow.
            console.error("Error sending message:", error);
            stopPolling();
            setLoading(false);
            // The user's own message is deliberately left in place. Removing it made a
            // recoverable failure look as though the message had never been sent.
            showToast(extractErrorDetail(error, "Failed to send message"), "error");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    if (initializing) {
        return (
            <div className="chat-container" style={{ justifyContent: "center", alignItems: "center" }}>
                <Spinner label="Loading conversation..." />
            </div>
        );
    }

    return (
        <div className="chat-container">
            <div className="messages" ref={messagesContainerRef}>
                <div className="message-wrapper">
                    {isFileAnalysis && loading && !messages.some(msg => msg.role === ConversationRole.ASSISTANT) && (
                        <div className="architecture-analysis-notice">
                            <div className="architecture-analysis-notice-title">
                                Analyzing your architecture document
                            </div>
                            <div className="architecture-analysis-notice-body">
                                Analysis in progress. This comprehensive security assessment may take
                                up to 15 minutes depending on document size and complexity.
                            </div>
                            <div className="architecture-analysis-notice-warning">
                                Please do not close the browser or navigate away.
                            </div>
                        </div>
                    )}

                    {messages
                        .filter(msg => {
                            // During file analysis, hide ONLY the auto-generated analysis message
                            // Keep all previous conversation history visible (previous uploads, questions, responses)
                            if (isFileAnalysis && loading && msg.role === ConversationRole.USER) {
                                // Only hide if it contains the auto-generated analysis request text
                                return !msg.content.includes("UPLOADED ARCHITECTURE DOCUMENT:");
                            }
                            return true;
                        })
                        .map((msg, index) => {
                            const isUser = msg.role === ConversationRole.USER;
                            const agentName = !isUser ? getAgentNameForMessage(msg) : "";

                            return (
                                <div
                                    key={msg.id || index}
                                    className={`message ${isUser ? "user" : "assistant"}`}
                                >
                                    {!isUser && (
                                        <div className="plan-chat-header">
                                            <div className="plan-chat-speaker">
                                                <Body1 className="speaker-name">
                                                    {agentName}
                                                </Body1>
                                            </div>
                                        </div>
                                    )}

                                    <Body1>
                                        <div className="plan-chat-message-content">
                                            {!isUser && isCloudArchitectureMessage(msg) && (
                                                <MessageBar intent="info" style={{ marginBottom: "16px" }}>
                                                    <MessageBarBody>
                                                        <MessageBarTitle>Disclaimer</MessageBarTitle>
                                                        <div style={{ marginTop: "8px", fontSize: "13px", lineHeight: "1.5" }}>
                                                            The analysis results below are provided by the <strong>Cloud Architecture Analysis feature</strong>.
                                                            <br />
                                                            <strong>Please note:</strong>
                                                            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                                                                <li>Results should be validated by qualified cloud architects and security professionals</li>
                                                                <li>This is not a substitute for comprehensive security audits</li>
                                                                <li>Always verify recommendations align with your organization's policies and compliance requirements</li>
                                                            </ul>
                                                        </div>
                                                    </MessageBarBody>
                                                </MessageBar>
                                            )}

                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                rehypePlugins={[rehypePrism]}
                                                components={markdownComponents}
                                            >
                                                {TaskService.cleanHRAgent(msg.content) || ""}
                                            </ReactMarkdown>

                                            {!isUser && (
                                                <div className="assistant-footer">
                                                    <div className="assistant-actions">
                                                        <Button
                                                            onClick={() =>
                                                                msg.content &&
                                                                navigator.clipboard.writeText(msg.content)
                                                            }
                                                            title="Copy Response"
                                                            appearance="subtle"
                                                            style={{ height: 28, width: 28 }}
                                                            icon={<Copy />}
                                                        />
                                                        {msg.content && msg.content.includes('Security Assessment Score') && (
                                                            <Button
                                                                onClick={() => exportToPDF(msg.content, agentName)}
                                                                title="Export to PDF"
                                                                appearance="subtle"
                                                                style={{ height: 28, width: 28 }}
                                                                icon={<DocumentPdf />}
                                                            />
                                                        )}
                                                    </div>
                                                    {msg.metadata?.generation_time_ms !== undefined && (
                                                        <span style={{
                                                            fontSize: '11px',
                                                            color: '#8a8a8a',
                                                        }}>
                                                            {formatGenerationTime(msg.metadata.generation_time_ms)}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Show plan card inline if this message created a plan */}
                                            {msg.plan_id && msg.message_type === MessageType.PLAN_CREATED && (
                                                <PlanCard
                                                    planId={msg.plan_id}
                                                    sessionId={sessionId}
                                                    onPlanCompleted={checkForNewMessages}
                                                    hasResultMessage={messages.some(
                                                        (m: ConversationMessage) => m.plan_id === msg.plan_id && m.message_type === MessageType.TEXT && m.role === ConversationRole.ASSISTANT
                                                    )}
                                                />
                                            )}
                                        </div>
                                    </Body1>

                                    {/* Show timestamp for user messages */}
                                    {isUser && msg.timestamp && (
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#8a8a8a',
                                            marginTop: '4px',
                                            textAlign: 'right'
                                        }}>
                                            {formatMessageTimestamp(msg.timestamp)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    {/* Show loading indicator when waiting for response */}
                    {loading && (
                        <div className="message assistant">
                            <Body1>
                                <div className="plan-chat-message-content">
                                    <div className="loading-row">
                                        <div className="typing-indicator">
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                        <div key={loadingText} className="loading-status-text">
                                            {loadingText}
                                        </div>
                                    </div>
                                </div>
                            </Body1>
                        </div>
                    )}
                </div>
            </div>

            {showScrollButton && (
                <Tag
                    onClick={scrollToBottom}
                    className="scroll-to-bottom plan-chat-scroll-button"
                    shape="circular"
                    style={{
                        bottom: inputHeight,
                        position: "absolute",
                        right: 16,
                        zIndex: 5,
                    }}
                >
                    Back to bottom
                </Tag>
            )}

            <div ref={inputContainerRef} className="plan-chat-input-container">
                <div className="plan-chat-input-wrapper">
                    <ChatInput
                        value={input}
                        onChange={setInput}
                        onEnter={handleSendMessage}
                        disabledChat={loading}
                        placeholder="Ask a question or request an action..."
                    >
                        <FileUploadButton
                            sessionId={sessionId}
                            onUploadSuccess={(fileInfo) => {
                                // Navigate to new session when file is uploaded
                                const uploadSessionId = fileInfo.session_id;
                                if (uploadSessionId && uploadSessionId !== sessionId) {
                                    showToast("Starting new analysis in a new session...", "success");
                                    setTimeout(() => {
                                        navigate(`/conversation/${uploadSessionId}`, {
                                            state: {
                                                uploadedFile: fileInfo,
                                                isNewConversation: true
                                            }
                                        });
                                    }, 1500);
                                } else {
                                    showToast("File uploaded successfully", "success");
                                }
                            }}
                            onUploadError={(error) => showToast(error, "error")}
                            disabled={loading}
                        />
                        <Button
                            appearance="primary"
                            onClick={handleSendMessage}
                            icon={<Send />}
                            disabled={loading}
                        />
                    </ChatInput>
                </div>
            </div>
        </div>
    );
};

export default ConversationView;
